import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Compass, Shield, Users, Building2, Layers, Cpu, Globe2, Lightbulb, ShieldCheck } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { MaskedLines } from "@/components/motion";

const IMG = "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const IMG2 = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const VALUES = [
  { t: "Domain first", d: "Every consultant is fluent in the trading domain before they write a line of code or a page of documentation." },
  { t: "Craft over speed", d: "We optimise for durable outcomes. We would rather ship one great deliverable than ten average ones." },
  { t: "Independent counsel", d: "We give clients the honest answer — even when it is inconvenient — because trust compounds over decades." },
  { t: "Systems thinking", d: "Trading platforms are part of an operating model. We design for the whole system, not just the ticket in front of us." },
  { t: "Continuous learning", d: "Markets, regulations and technologies never stand still. Neither do we." },
  { t: "Global, respectful", d: "We work across time zones, cultures and regulatory regimes with the same standard of respect and precision." },
];

const PHILOSOPHY = [
  { t: "Specialist, not generalist", d: "We focus exclusively on Energy Trading & Commodity Trading technology. Depth beats breadth." },
  { t: "Practitioners, not slideware", d: "Our consultants have shipped, run and refined the platforms they advise on." },
  { t: "Outcomes, not activity", d: "We commit to business KPIs — not just headcount and hours." },
  { t: "Independence, always", d: "We are not incentivised by any vendor. Our advice is calibrated to your reality alone." },
];

const METHODOLOGY = [
  { n: "01", t: "Listen", d: "We start with your business — commercial pressures, operating rhythm, regulatory landscape — before opening any platform." },
  { n: "02", t: "Diagnose", d: "A structured assessment of platform, process and people, benchmarked against industry-leading practice." },
  { n: "03", t: "Design", d: "Reference architectures, functional blueprints and delivery roadmaps grounded in your reality — not templates." },
  { n: "04", t: "Deliver", d: "Senior practitioners on the floor, working alongside your team. No handoffs to juniors. No surprises at go-live." },
  { n: "05", t: "Sustain", d: "Managed services, continuous improvement and knowledge transfer that leaves your team stronger than we found it." },
];

const WHY = [
  { icon: Shield, t: "Trusted independent counsel", d: "We work in the client's interest, not the vendor's. Boards rely on us for the answer they can defend." },
  { icon: Users, t: "Senior practitioner delivery", d: "Every engagement is led by senior consultants who have shipped comparable programs at global trading houses." },
  { icon: Globe2, t: "Global reach, remote-first", d: "We work with organisations across markets and time zones — bringing local nuance where it matters." },
  { icon: Lightbulb, t: "Innovation without hype", d: "We industrialise AI, cloud and data in trading — not as buzzwords, but as durable operational capability." },
  { icon: ShieldCheck, t: "Outcome-based accountability", d: "We measure success in business KPIs — trading-day availability, cycle-time compression, value realised." },
  { icon: Building2, t: "Enterprise-grade delivery", d: "Governance, controls and documentation that satisfy the most demanding board, audit and regulator." },
];

const INDUSTRIES_SUPPORTED = ["Oil & Gas", "LNG", "Power", "Utilities", "Metals", "Mining", "Agriculture", "Renewable Energy", "Trading Houses", "Energy Producers", "Energy End-Users", "Global Consulting Partners"];

const TECH_EXPERTISE = ["Openlink Endur", "Allegro", "RightAngle", "AspectCTRM", "SAP", "Power BI", "Snowflake", "Azure", "AWS", "Databricks"];

export default function About() {
  useSEO({
    title: "About EnPrex Services — Global Specialist Trading Technology Consultancy",
    description: "EnPrex Services is a global specialist consulting firm dedicated to Energy Trading and Commodity Trading technology — practitioner-led advisory, implementation and managed services.",
    path: "/about",
  });
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src={IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/60 via-[#050A14]/90 to-[#050A14]" />
        </div>
        <div className="container-x relative pt-16 pb-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— About EnPrex Services</div>
            <h1 className="font-display text-5xl md:text-7xl font-light text-white tracking-tighter leading-[0.98] max-w-5xl">
              <MaskedLines delay={0.15} lines={[<>A specialist consultancy for</>, <><span className="italic">energy &amp; commodity</span> trading.</>]} />
            </h1>
          </motion.div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="container-x py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-4">01 — Who We Are</div>
          <h2 className="font-display text-4xl md:text-5xl font-light text-white tracking-tight leading-[1.05]">
            Trading technology, at <span className="italic">enterprise standard</span>.
          </h2>
        </div>
        <div className="lg:col-span-8 space-y-6 text-lg text-slate-300 font-light leading-relaxed">
          <p>EnPrex Services is a global specialist consulting firm dedicated to Energy Trading and Commodity Trading technology. We help energy companies, commodity trading houses, utilities, producers and consulting partners transform how they trade — with the discipline, independence and depth of a firm built for the long term.</p>
          <p>Our people are practitioners. We have designed, implemented, upgraded, migrated and operated trading platforms at some of the most demanding organizations in the world. That practical fluency is the difference between a slide deck and a system that traders actually use.</p>
          <p>We work exclusively in this domain. Every engagement, every insight, every training module is grounded in the reality of how modern trading businesses actually work.</p>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="container-x py-16 grid lg:grid-cols-2 gap-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="border border-white/5 bg-white/[0.02] p-10">
          <Target className="text-[#D4AF37] mb-6" size={32} strokeWidth={1.2} />
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-3">02 — Our Mission</div>
          <h2 className="font-display text-3xl md:text-4xl font-light text-white tracking-tight leading-tight">
            To help energy and commodity organizations trade with more precision, confidence and speed.
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed">
            We do this by pairing the world's most experienced ETRM/CTRM practitioners with modern cloud, data and AI foundations — and by holding ourselves accountable for business outcomes, not activity.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="border border-white/5 bg-white/[0.02] p-10">
          <Eye className="text-[#D4AF37] mb-6" size={32} strokeWidth={1.2} />
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-3">03 — Our Vision</div>
          <h2 className="font-display text-3xl md:text-4xl font-light text-white tracking-tight leading-tight">
            To become the definitive global partner for trading technology transformation.
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed">
            Over time, EnPrex will extend beyond consulting into AI products, SaaS accelerators, a learning academy and knowledge platforms — all anchored in deep trading domain expertise.
          </p>
        </motion.div>
      </section>

      {/* CONSULTING PHILOSOPHY */}
      <section className="container-x py-24 border-t border-white/5">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-14 max-w-4xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">04 — Our Consulting Philosophy</div>
          <h2 className="font-display text-4xl md:text-6xl font-light text-white tracking-tighter leading-[1.05]">
            Four convictions that shape <span className="italic">every engagement</span>.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-px bg-white/5">
          {PHILOSOPHY.map((p, i) => (
            <motion.div key={p.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 2) * 0.05 }} className="bg-[#050A14] p-10">
              <Compass className="text-[#D4AF37] mb-5" size={26} strokeWidth={1.3} />
              <h3 className="font-display text-2xl text-white font-medium">{p.t}</h3>
              <p className="mt-3 text-slate-400 leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* DELIVERY APPROACH — METHODOLOGY */}
      <section className="container-x py-24 border-t border-white/5">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 max-w-4xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">05 — Our Delivery Approach</div>
          <h2 className="font-display text-4xl md:text-6xl font-light text-white tracking-tighter leading-[1.05]">
            A methodology built for <span className="italic">enterprise ambition</span>.
          </h2>
          <p className="mt-6 text-slate-400 text-lg font-light leading-relaxed">
            Every EnPrex engagement — whether a two-week diagnostic or a multi-year transformation — follows the same disciplined arc. It is what allows us to be predictable at scale.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-5 gap-px bg-white/5">
          {METHODOLOGY.map((m, i) => (
            <motion.div key={m.n} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="bg-[#050A14] p-8">
              <div className="font-display text-4xl text-[#D4AF37] font-light">{m.n}</div>
              <h3 className="font-display text-lg text-white mt-4 font-medium">{m.t}</h3>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">{m.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CLIENTS CHOOSE */}
      <section className="container-x py-24 border-t border-white/5">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 max-w-4xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">06 — Why Clients Choose EnPrex</div>
          <h2 className="font-display text-4xl md:text-6xl font-light text-white tracking-tighter leading-[1.05]">
            Depth of a boutique. <span className="italic">Reach</span> of a global firm.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <motion.div key={w.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05 }} className="bg-[#050A14] p-10">
                <Icon className="text-[#D4AF37] mb-5" size={26} strokeWidth={1.3} />
                <h3 className="font-display text-xl text-white font-medium">{w.t}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{w.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="container-x py-24 border-t border-white/5">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 max-w-4xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">07 — Core Values</div>
          <h2 className="font-display text-4xl md:text-6xl font-light text-white tracking-tighter leading-[1.05]">
            Six principles, <span className="italic">every day</span>.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {VALUES.map((v, i) => (
            <motion.div key={v.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05 }} className="bg-[#050A14] p-10">
              <div className="font-mono text-xs text-[#D4AF37] tracking-widest">0{i + 1}</div>
              <h3 className="font-display text-xl text-white mt-3 font-medium">{v.t}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{v.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES + TECH */}
      <section className="container-x py-24 border-t border-white/5 grid lg:grid-cols-2 gap-16">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">08 — Industries We Support</div>
          <h3 className="font-display text-3xl md:text-4xl font-light text-white tracking-tight leading-tight mb-8">
            The organizations we serve.
          </h3>
          <div className="flex flex-wrap gap-3">
            {INDUSTRIES_SUPPORTED.map((ind) => (
              <span key={ind} className="border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-300">{ind}</span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">09 — Technology Expertise</div>
          <h3 className="font-display text-3xl md:text-4xl font-light text-white tracking-tight leading-tight mb-8">
            The platforms we know inside-out.
          </h3>
          <div className="flex flex-wrap gap-3">
            {TECH_EXPERTISE.map((t) => (
              <span key={t} className="border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-300">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED ENGAGEMENT PATTERNS */}
      <section className="border-t border-white/5">
        <div className="container-x py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-14 max-w-4xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">10 — Selected Engagement Patterns</div>
            <h2 className="font-display text-4xl md:text-6xl font-light text-white tracking-tighter leading-[1.05]">
              The kinds of problems we <span className="italic">solve</span>.
            </h2>
            <p className="mt-6 text-slate-400 text-lg font-light leading-relaxed">
              The engagements below are anonymised composites of the recurring problem patterns our practitioners have worked on across the energy and commodity trading industry. Client-specific references are available under NDA during the engagement conversation.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-px bg-white/5">
            {[
              {
                area: "ETRM / Endur",
                title: "Endur upgrade & platform re-baseline",
                problem: "A trading business running a heavily customised Endur estate several versions behind current, with an unclear upgrade path and rising run-cost.",
                approach: "Structured estate assessment, upgrade decision paper, automated regression pack across the trade lifecycle, and rehearsed cutover with staged parallel run.",
                outcome: "Cleaner customization footprint post-upgrade, predictable cutover with rehearsed rollback, and a business-endorsed roadmap for the next two years.",
              },
              {
                area: "CTRM / Physical",
                title: "CTRM modernisation for a physical trading desk",
                problem: "A physical commodity desk operating across multiple products with fragmented position, logistics and settlement workflows spread across spreadsheets and legacy tooling.",
                approach: "Target operating model, reference architecture, provisional pricing and QP redesign, and phased migration to a modern CTRM platform with integrated logistics.",
                outcome: "Consolidated position and P&L view, materially reduced manual controls, and the platform capability to onboard adjacent commodities without re-architecture.",
              },
              {
                area: "Managed Services",
                title: "Outcome-priced managed services transition",
                problem: "An incumbent application support arrangement priced on tickets and headcount, with an improvement backlog perpetually deferred and weak reporting to sponsors.",
                approach: "SLA-backed managed services operating model, transition plan from incumbent, continuous improvement backlog with quarterly business reviews.",
                outcome: "Higher trading-day availability, transparent value stories to sponsors each quarter, and predictable cost with capacity that flexes to demand.",
              },
            ].map((c) => (
              <div key={c.title} className="bg-[#050A14] p-10">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-4">{c.area}</div>
                <h3 className="font-display text-xl text-white font-medium leading-tight">{c.title}</h3>
                <div className="mt-6 space-y-4">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Problem</div>
                    <p className="text-sm text-slate-300 leading-relaxed">{c.problem}</p>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Approach</div>
                    <p className="text-sm text-slate-300 leading-relaxed">{c.approach}</p>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Outcome</div>
                    <p className="text-sm text-slate-300 leading-relaxed">{c.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-slate-500 font-mono uppercase tracking-widest">
            — Anonymised engagement patterns · Client-specific references shared under NDA
          </p>
        </div>
      </section>

      {/* REFERENCES & CONFIDENTIALITY */}
      <section className="border-t border-white/5 bg-[#0B1F3A]/30">
        <div className="container-x py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">11 — References & Confidentiality</div>
            <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tighter leading-[1.05]">
              We work under <span className="italic">strict client confidentiality</span>.
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed max-w-2xl">
              Our clients trust us with commercially sensitive information. As a matter of practice, we do not publicly list client names, publish quotable testimonials, or share project detail without explicit permission. Verifiable references are available under NDA during the qualification stage of a serious engagement conversation.
            </p>
          </div>
          <div className="lg:col-span-5 lg:justify-self-end">
            <Link to="/contact" data-testid="about-cta-references" className="inline-flex items-center gap-3 border border-[#D4AF37]/50 text-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              Request References <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* LONG-TERM VISION */}
      <section className="border-t border-white/5">
        <div className="container-x py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">12 — Our Long-term Vision</div>
            <h2 className="font-display text-4xl md:text-6xl font-light text-white tracking-tighter leading-[1.05]">
              From consultancy to <span className="italic">global platform</span>.
            </h2>
            <p className="mt-6 text-slate-400 text-lg font-light leading-relaxed">
              The next chapter of EnPrex extends beyond consulting. We are building the foundations of a global platform for trading technology — combining specialist services, AI-native products, a learning academy, and knowledge experiences for practitioners around the world.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { Icon: Cpu, l: "AI Products" },
                { Icon: Layers, l: "SaaS Accelerators" },
                { Icon: Shield, l: "Managed Services Portal" },
                { Icon: Globe2, l: "Learning Academy" },
              ].map(({ Icon, l }) => (
                <div key={l} className="flex items-center gap-3 border border-white/5 bg-white/[0.02] p-4">
                  <Icon className="text-[#D4AF37]" size={20} strokeWidth={1.3} />
                  <span className="text-sm text-slate-300 font-medium">{l}</span>
                </div>
              ))}
            </div>
            <Link to="/contact" data-testid="about-cta-partner" className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#FDE047] transition-colors">
              Partner with us <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-6 aspect-[4/3] overflow-hidden">
            <img src={IMG2} alt="EnPrex vision" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
